/**
 * Libreria drill di tiro: elenco, editor, piantina.
 *
 * Perché esiste: senza, ogni drill si riscrive a mano a ogni settimana, e
 * soprattutto **non c'è niente da collegare** come benchmark nel profilo
 * prestativo. Un El Presidente prescritto tre volte sono tre esercizi
 * scollegati; come drill di libreria è uno, con il suo storico.
 *
 * Due cose che il drill ha e la prescrizione del singolo giorno no: **video e
 * descrizione**. Sono la differenza fra un drill scritto e un drill che
 * l'atleta capisce senza il coach accanto.
 *
 * **L'editor è lo stesso del ProgramBuilder**, pezzo per pezzo: le sezioni
 * «Quanto» e «Stage» sono i componenti condivisi `SeriesPlanSection` e
 * `StageSetupSection`, le etichette e gli elenchi di regolamento vengono da
 * `formPrimitives`. Prima la libreria aveva campi senza etichetta, nessuna
 * tabella dei bersagli e un numero di serie senza distanze — e al test è
 * uscito che «i due designer devono comportarsi uguali». Il modo di
 * garantirlo è che siano lo stesso codice.
 *
 * Scope `personal | global` come la libreria conditioning: l'admin pubblica gli
 * standard, il coach se li copia e li adatta senza poterli alterare per tutti.
 */
import { useCallback, useEffect, useState } from "react";
import {
	BookOpen,
	ChevronDown,
	ChevronRight,
	Copy,
	Loader2,
	Pencil,
	Plus,
	Star,
	Trash2,
	Video,
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Badge } from "./ui/badge";
import { Card, CardContent } from "./ui/card";
import { Textarea } from "./programBuilder/Textarea";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "./ui/select";
import { fetchAPI, selectableSports } from "../lib/utils";
import { useAuth } from "../contexts/AuthContext";
import { useLanguage } from "../contexts/LanguageContext";
import {
	SHOOTING_FOCUS_OPTIONS,
	formatShootingTargets,
} from "../lib/programBuilderUtils";
import { firearmTypeIcon } from "./shooting/weaponIcons";
import {
	Field,
	ScoringSystemZones,
	Section,
	StartConditionsSection,
} from "./shooting/formPrimitives";
import {
	DryFireQuantityFields,
	SeriesPlanSection,
} from "./shooting/SeriesPlanSection";
import { StageSetupSection } from "./shooting/StageSetupSection";
import { plannedSeriesCount, roundsPerSeries } from "./shooting/shootingPlan";

const MODES = [
	{ value: "dry_fire", label: "Dry fire" },
	{ value: "live_fire", label: "Live fire" },
];

const FIREARM_TYPES = [
	{ value: "handgun", labelKey: "pbShootFirearmHandgun" },
	{ value: "rifle", labelKey: "pbShootFirearmRifle" },
	{ value: "shotgun", labelKey: "pbShootFirearmShotgun" },
	{ value: "pcc", labelKey: "pbShootFirearmPcc" },
];

const emptyDrill = () => ({
	name: "",
	discipline: "",
	description: "",
	video_url: "",
	is_benchmark: false,
	library_scope: "personal",
	prescription: { mode: "live_fire", targets: [], stage_layout: [] },
});

const inputClass = "bg-secondary border-transparent rounded-sm";

export function ShootingDrillsTab() {
	const { user } = useAuth();
	const { t } = useLanguage();
	const [drills, setDrills] = useState([]);
	const [sports, setSports] = useState([]);
	const [scoringSystems, setScoringSystems] = useState([]);
	const [loading, setLoading] = useState(true);
	const [saving, setSaving] = useState(false);
	const [draft, setDraft] = useState(null);
	const [detailsOpen, setDetailsOpen] = useState(false);

	const load = useCallback(async () => {
		setLoading(true);
		try {
			const [list, sportsData, reference] = await Promise.all([
				fetchAPI("/shooting/drills").catch(() => []),
				fetchAPI("/users/sports/list").catch(() => []),
				fetchAPI("/shooting/reference").catch(() => ({})),
			]);
			setDrills(Array.isArray(list) ? list : []);
			setSports(
				(Array.isArray(sportsData) ? sportsData : []).filter((s) =>
					(s.families || []).includes("shooting"),
				),
			);
			setScoringSystems(reference?.scoring_systems || []);
		} finally {
			setLoading(false);
		}
	}, []);

	useEffect(() => {
		load();
	}, [load]);

	const prescription = draft?.prescription || {};
	const isLive = prescription.mode !== "dry_fire";

	const patchPrescription = (changes) =>
		setDraft((prev) => ({
			...prev,
			prescription: { ...(prev.prescription || {}), ...changes },
		}));

	const selectedSystem = scoringSystems.find(
		(system) => system.system_id === prescription.scoring_system_id,
	);
	// Cosa manca per salvare: si scrive accanto al bottone, non si spegne il
	// bottone in silenzio.
	const missing = draft
		? [
				!draft.name?.trim() && "name",
				isLive &&
					prescription.prescription_format !== "free" &&
					!prescription.scoring_system_id &&
					"scoring_system",
			].filter(Boolean)
		: [];

	const handleSave = async () => {
		if (saving || !draft) return;
		if (!draft.name.trim()) return toast.error(t("pbNameTheDrill"));
		setSaving(true);
		try {
			const payload = { ...draft, name: draft.name.trim() };
			if (draft.drill_id) {
				await fetchAPI(`/shooting/drills/${draft.drill_id}`, {
					method: "PUT",
					body: JSON.stringify(payload),
				});
			} else {
				await fetchAPI("/shooting/drills", {
					method: "POST",
					body: JSON.stringify(payload),
				});
			}
			toast.success(t("pbShootDrillSaved"));
			setDraft(null);
			await load();
		} catch (error) {
			toast.error(`${t("error")}: ${error.message}`);
		} finally {
			setSaving(false);
		}
	};

	const handleCopy = async (drill) => {
		try {
			await fetchAPI(`/shooting/drills/${drill.drill_id}/copy`, {
				method: "POST",
			});
			toast.success(t("pbShootDrillCopied"));
			await load();
		} catch (error) {
			toast.error(`${t("error")}: ${error.message}`);
		}
	};

	const handleDelete = async (drill) => {
		try {
			await fetchAPI(`/shooting/drills/${drill.drill_id}`, {
				method: "DELETE",
			});
			toast.success(t("pbShootDrillDeactivated"));
			await load();
		} catch (error) {
			toast.error(`${t("error")}: ${error.message}`);
		}
	};

	const canEdit = (drill) =>
		user?.is_admin ||
		(drill.library_scope !== "global" &&
			(drill.owner_id === user?.user_id || drill.coach_id === user?.user_id));

	if (loading) {
		return (
			<div className="flex items-center gap-2 py-10 justify-center text-sm text-muted-foreground">
				<Loader2 className="w-4 h-4 animate-spin" /> {t("loading")}
			</div>
		);
	}

	// ── Editor ──
	if (draft) {
		return (
			<div className="space-y-3">
				<div className="flex items-center justify-between gap-2">
					<h2 className="font-heading text-lg">
						{draft.drill_id ? t("pbShootEditDrill") : t("pbShootNewDrill")}
					</h2>
					<Button
						variant="ghost"
						size="sm"
						className="rounded-sm"
						onClick={() => setDraft(null)}
					>
						{t("cancel")}
					</Button>
				</div>

				<div className="grid grid-cols-2 gap-1 bg-secondary p-1 rounded-sm">
					{MODES.map((m) => (
						<button
							key={m.value}
							type="button"
							onClick={() => patchPrescription({ mode: m.value })}
							className={`py-2 rounded-sm text-xs font-medium ${
								(prescription.mode || "live_fire") === m.value
									? "bg-volt text-white"
									: "text-muted-foreground"
							}`}
						>
							{m.label}
						</button>
					))}
				</div>

				{/* ── IL DRILL ── */}
				<Section title={t("pbShootDrillSection")}>
					<Field label={t("pbShootDrillName")}>
						<Input
							value={draft.name}
							onChange={(e) => setDraft({ ...draft, name: e.target.value })}
							className={`${inputClass} h-11`}
							placeholder="Es: El Presidente"
						/>
					</Field>
					<div className="grid grid-cols-2 gap-2">
						<Field label={t("pbShootDiscipline")}>
							<Select
								value={draft.discipline || "__none__"}
								onValueChange={(v) =>
									setDraft({ ...draft, discipline: v === "__none__" ? "" : v })
								}
							>
								<SelectTrigger className={inputClass}>
									<SelectValue placeholder={t("pbShootDiscipline")} />
								</SelectTrigger>
								<SelectContent className="bg-card border-border">
									<SelectItem value="__none__">{t("pbShootAllDisciplines")}</SelectItem>
									{/* Una disciplina disattivata resta solo se e' gia' quella del drill. */}
									{selectableSports(sports, [draft.discipline]).map((sport) => (
										<SelectItem key={sport.value} value={sport.value}>
											{sport.label}
										</SelectItem>
									))}
								</SelectContent>
							</Select>
						</Field>
						<Field label={t("pbShootFirearm")}>
							<Select
								value={prescription.firearm_type || "__none__"}
								onValueChange={(v) =>
									patchPrescription({
										firearm_type: v === "__none__" ? null : v,
									})
								}
							>
								<SelectTrigger className={inputClass}>
									<SelectValue placeholder={t("pbShootFirearm")} />
								</SelectTrigger>
								<SelectContent className="bg-card border-border">
									<SelectItem value="__none__">{t("pbShootFirearmUnset")}</SelectItem>
									{FIREARM_TYPES.map((ft) => (
										<SelectItem key={ft.value} value={ft.value}>
											{t(ft.labelKey)}
										</SelectItem>
									))}
								</SelectContent>
							</Select>
						</Field>
					</div>
					<Field label={t("pbShootFocus")}>
						<Select
							value={prescription.drill_focus || "__none__"}
							onValueChange={(v) =>
								patchPrescription({ drill_focus: v === "__none__" ? null : v })
							}
						>
							<SelectTrigger className={inputClass}>
								<SelectValue placeholder={t("pbShootFocus")} />
							</SelectTrigger>
							<SelectContent className="bg-card border-border">
								<SelectItem value="__none__">{t("pbShootFocusUnset")}</SelectItem>
								{SHOOTING_FOCUS_OPTIONS.map((f) => (
									<SelectItem key={f.value} value={f.value}>
										{t(f.labelKey)}
									</SelectItem>
								))}
							</SelectContent>
						</Select>
					</Field>

					{/* Video e descrizione: è ciò che rende un drill eseguibile
					    senza il coach accanto. */}
					<Field
						label={
							<span className="flex items-center gap-1.5">
								<Video className="w-3.5 h-3.5" /> {t("pbShootVideo")}
							</span>
						}
					>
						<Input
							value={draft.video_url || ""}
							onChange={(e) => setDraft({ ...draft, video_url: e.target.value })}
							className={inputClass}
							placeholder={t("pbShootVideoPlaceholder")}
						/>
					</Field>
					<Field label={t("pbShootDescription")}>
						<Textarea
							value={draft.description || ""}
							onChange={(e) =>
								setDraft({ ...draft, description: e.target.value })
							}
							className={inputClass}
							rows={3}
							placeholder={t("pbShootDescriptionPlaceholder")}
						/>
					</Field>
				</Section>

				{/* ── QUANTO ── */}
				{isLive ? (
					<SeriesPlanSection
						shooting={prescription}
						onChange={patchPrescription}
						t={t}
					/>
				) : (
					<DryFireQuantityFields
						reps={prescription.reps_planned}
						onRepsChange={(value) =>
							patchPrescription({
								reps_planned: value ? parseInt(value, 10) : null,
							})
						}
						parTime={prescription.par_time_seconds}
						onParTimeChange={(value) =>
							patchPrescription({ par_time_seconds: value })
						}
						t={t}
					/>
				)}

				{/* ── STAGE ── */}
				<StageSetupSection
					shooting={prescription}
					onChange={patchPrescription}
					discipline={draft.discipline || "ipsc"}
					t={t}
				/>

				{/* ── CONDIZIONI DI PARTENZA ──
				    In chiaro, non dietro «altri dettagli»: descrivono
				    l'esercizio quanto i bersagli e il par time. */}
				<StartConditionsSection
					shooting={prescription}
					onChange={patchPrescription}
					className={inputClass}
					t={t}
				/>

				{/* ── COME SI GIUDICA ── */}
				{isLive && (
					<Section title={t("pbShootHowJudged")} hint={t("pbShootHowJudgedHint")}>
						<Field label={t("pbShootScoringSystem")}>
							<Select
								value={prescription.scoring_system_id || "__none__"}
								onValueChange={(v) =>
									patchPrescription({
										scoring_system_id: v === "__none__" ? null : v,
									})
								}
							>
								<SelectTrigger className={`${inputClass} h-11`}>
									<SelectValue placeholder={t("pbShootScoringSystem")} />
								</SelectTrigger>
								<SelectContent className="bg-card border-border">
									<SelectItem value="__none__">{t("noneOption")}</SelectItem>
									{scoringSystems.map((sys) => (
										<SelectItem key={sys.system_id} value={sys.system_id}>
											{sys.label}
										</SelectItem>
									))}
								</SelectContent>
							</Select>
						</Field>
						{selectedSystem ? (
							<ScoringSystemZones system={selectedSystem} />
						) : (
							<p className="text-xs text-amber-400">{t("pbShootNoScoringWarning")}</p>
						)}
					</Section>
				)}

				{/* ── OBIETTIVO E REGISTRAZIONE ── */}
				<Section title={t("pbShootGoalSection")}>
					<Field label={t("pbShootAthleteGoal")} hint={t("pbShootAthleteGoalHint")}>
						<Input
							value={prescription.athlete_goal ?? prescription.success_criteria ?? ""}
							onChange={(e) =>
								patchPrescription({
									athlete_goal: e.target.value,
									success_criteria: null,
								})
							}
							className={inputClass}
							placeholder={
								isLive
									? t("pbShootSuccessCriteriaLive")
									: t("pbShootSuccessCriteriaDry")
							}
						/>
					</Field>
					<Field
						label={t("pbShootScoringInstructions")}
						hint={t("pbShootScoringInstructionsHint")}
					>
						<Input
							value={prescription.scoring_instructions || ""}
							onChange={(e) =>
								patchPrescription({ scoring_instructions: e.target.value })
							}
							className={inputClass}
							placeholder={t("pbShootScoringInstructionsPlaceholder")}
						/>
					</Field>
					<Field label={t("pbShootCoachNotes")} hint={t("pbShootCoachNotesHint")}>
						<Textarea
							value={prescription.coach_notes || ""}
							onChange={(e) => patchPrescription({ coach_notes: e.target.value })}
							className={inputClass}
							rows={2}
							placeholder={t("pbShootCoachNotesPlaceholder")}
						/>
					</Field>
				</Section>

				{/* ── ALTRI DETTAGLI ── */}
				<button
					type="button"
					onClick={() => setDetailsOpen((open) => !open)}
					className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground"
				>
					{detailsOpen ? (
						<ChevronDown className="w-3.5 h-3.5" />
					) : (
						<ChevronRight className="w-3.5 h-3.5" />
					)}
					{t("pbShootOtherDetails")}
				</button>
				{detailsOpen && (
					<div className="space-y-2.5 pl-1">
						<Field label={t("pbShootSetupNotes")} hint={t("pbShootSetupNotesHint")}>
							<Input
								value={prescription.target_setup || ""}
								onChange={(e) =>
									patchPrescription({ target_setup: e.target.value })
								}
								className={inputClass}
								placeholder={t("pbShootSetupNotes")}
							/>
						</Field>
						{/* Il settore dello stage non sta qui: dice quale parte
						    dell'allestimento **della giornata** usa il drill, e
						    si sceglie nel programma, giorno per giorno. */}
					</div>
				)}

				<Button
					type="button"
					size="sm"
					variant={draft.is_benchmark ? "default" : "secondary"}
					className={`rounded-sm w-full ${draft.is_benchmark ? "bg-volt hover:bg-volt/90" : ""}`}
					onClick={() => setDraft({ ...draft, is_benchmark: !draft.is_benchmark })}
				>
					<Star className="w-3.5 h-3.5 mr-1.5" />
					{t("pbShootBenchmark")}
				</Button>

				{/* Cosa manca, scritto. Il bottone resta cliccabile e spiega. */}
				{missing.length > 0 && (
					<p className="text-[11px] text-amber-400 px-1">
						{t("pbShootMissing")}:{" "}
						{missing
							.map((key) =>
								key === "name" ? t("pbShootMissingName") : t("pbShootMissingScoring"),
							)
							.join(" · ")}
					</p>
				)}
				<Button
					onClick={handleSave}
					disabled={saving}
					className="w-full bg-volt hover:bg-volt/90 rounded-sm h-11"
				>
					{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : t("pbShootSaveDrill")}
				</Button>
			</div>
		);
	}

	// ── Elenco ──
	return (
		<div className="space-y-3">
			<Button
				className="w-full bg-volt hover:bg-volt/90 rounded-sm h-11"
				onClick={() => setDraft(emptyDrill())}
			>
				<Plus className="w-4 h-4 mr-1.5" />
				{t("pbShootNewDrill")}
			</Button>

			{drills.length === 0 ? (
				<Card className="bg-card border-border rounded-sm">
					<CardContent className="flex flex-col items-center gap-2 py-10 text-center">
						<BookOpen className="w-7 h-7 text-muted-foreground" />
						<p className="text-sm text-muted-foreground">{t("pbShootNoDrills")}</p>
						<p className="text-xs text-muted-foreground">{t("pbShootNoDrillsHint")}</p>
					</CardContent>
				</Card>
			) : (
				drills.map((drill) => {
					const WeaponIcon = firearmTypeIcon(
						drill.prescription?.firearm_type || "handgun",
					);
					const p = drill.prescription || {};
					const seriesCount = plannedSeriesCount(p);
					const rounds = roundsPerSeries(p);
					const targetsLine = formatShootingTargets(p.targets, t);
					return (
						<Card key={drill.drill_id} className="bg-card border-border rounded-sm">
							<CardContent className="p-3 flex items-start gap-3">
								<WeaponIcon size={18} className="text-volt shrink-0 mt-0.5" />
								<div className="flex-1 min-w-0">
									<div className="flex items-center gap-1.5 flex-wrap">
										<p className="font-medium truncate">{drill.name}</p>
										{drill.library_scope === "global" && (
											<Badge className="bg-muted text-muted-foreground border-0 text-[10px]">
												{t("pbShootStandardSuffix")}
											</Badge>
										)}
										{drill.is_benchmark && (
											<Badge className="bg-volt/20 text-volt border-0 text-[10px]">
												Benchmark
											</Badge>
										)}
									</div>
									<p className="text-xs text-muted-foreground mt-0.5">
										{p.mode === "dry_fire" ? "Dry fire" : "Live fire"}
										{seriesCount ? ` · ${seriesCount} ${t("pbRxSeries")}` : ""}
										{rounds
											? ` · ${rounds} ${t(seriesCount > 1 ? "pbRxShotsPerSeries" : "pbRxShots")}`
											: ""}
										{drill.video_url ? " · video" : ""}
									</p>
									{targetsLine && (
										<p className="text-[11px] text-muted-foreground truncate">
											{targetsLine}
										</p>
									)}
								</div>
								<div className="flex items-center gap-1 shrink-0">
									<Button
										variant="ghost"
										size="icon"
										className="rounded-sm h-8 w-8"
										title={t("pbShootCopyToLibrary")}
										onClick={() => handleCopy(drill)}
									>
										<Copy className="w-4 h-4" />
									</Button>
									{canEdit(drill) && (
										<>
											<Button
												variant="ghost"
												size="icon"
												className="rounded-sm h-8 w-8"
												onClick={() => {
													setDetailsOpen(false);
													setDraft({
														...drill,
														prescription: drill.prescription || {},
													});
												}}
											>
												<Pencil className="w-4 h-4" />
											</Button>
											<Button
												variant="ghost"
												size="icon"
												className="rounded-sm h-8 w-8 hover:text-red-500"
												onClick={() => handleDelete(drill)}
											>
												<Trash2 className="w-4 h-4" />
											</Button>
										</>
									)}
								</div>
							</CardContent>
						</Card>
					);
				})
			)}
		</div>
	);
}
